// React
import React, { useState, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// UI
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Chip,
  Tooltip,
  Typography
} from '@mui/material';
import PrimaryButton from '../ui/buttons/primaryButton';
import EditIcon from '@mui/icons-material/EditOutlined';
import TrashIcon from '@mui/icons-material/DeleteOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FlagIcon from '@mui/icons-material/OutlinedFlag';

// Services
import gameFormatService from '../../features/gameFormat/gameFormatService';
import PlayerViewDialog from './playerDialog';
import PlayerSwapDialog from './playerSwapDialog';
import PlayerStatusDialog from './playerStatusDialog';
import { removePlayerFromEvent } from '../../features/player/playerSlice';

const STATUS_COLOR = {
  Active: 'success',
  Withdrawn: 'warning',
  Disqualified: 'error'
};

// Players list + add/edit/remove/swap management UI for an event. Reused by
// `PlayersTeamsManager` (the standalone full-screen dialog on the event
// detail page) and by `ViewPlayersDialog` (opened via a button from the
// "Players & Teams" wizard step).
const PlayersPanel = ({ readOnly = false, canReplacePlayer = true }) => {
  const dispatch = useDispatch();

  const [openPlayer, setOpenPlayer] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState();
  const [openSwapDialog, setOpenSwapDialog] = useState(false);
  const [openPlayerStatusDialog, setOpenPlayerStatusDialog] = useState(false);
  const [playerToSwap, setPlayerToSwap] = useState(null);
  const [playerForStatus, setPlayerForStatus] = useState(null);
  const [removeConfirmationDialogOpen, setRemoveConfirmationDialogOpen] = useState(false);

  const event = useSelector((state) => state.event.event);
  const isSingles = event.gameFormat.gameType === gameFormatService.GameType.SINGLES;

  const playerTeamById = React.useMemo(() => {
    const map = new Map();
    // An active member wins over a bench spot on another team, so the row
    // keeps showing the team the player actually plays for.
    (event.teams || []).forEach((team) => {
      (team.members || []).forEach((member) => {
        map.set(member.id, { team, role: 'member' });
      });
    });
    (event.teams || []).forEach((team) => {
      (team.reserves || []).forEach((reserve) => {
        if (!map.has(reserve.id)) {
          map.set(reserve.id, { team, role: 'reserve' });
        }
      });
    });
    return map;
  }, [event.teams]);

  const handlePlayerAdd = () => {
    setSelectedPlayer();
    setOpenPlayer(true);
  };

  const handlePlayerEdit = (player) => {
    setSelectedPlayer(player);
    setOpenPlayer(true);
  };

  const handlePlayerSwap = (player) => {
    setPlayerToSwap(player);
    setOpenSwapDialog(true);
  };

  const handlePlayerStatus = (player) => {
    setPlayerForStatus(player);
    setOpenPlayerStatusDialog(true);
  };

  const handleRemoveConfirmationDialogOpen = (player) => {
    setSelectedPlayer(player);
    setRemoveConfirmationDialogOpen(true);
  };

  const handleRemoveConfirmationDialogClose = () => {
    setRemoveConfirmationDialogOpen(false);
  };

  const onPlayerRemove = async () => {
    dispatch(removePlayerFromEvent({ eventId: event.id, playerId: selectedPlayer.id }));
    handleRemoveConfirmationDialogClose();
  };

  const handleSwapDialogClose = () => {
    setOpenSwapDialog(false);
    setPlayerToSwap(null);
  };

  return (
    <Fragment>
      <Box display={'flex'} flexDirection={'column'}>
        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
          <Typography variant="h5">Players</Typography>
          {!readOnly ? (
            <PrimaryButton variant={'outlined'} onClick={handlePlayerAdd} color="primary">
              Add Player
            </PrimaryButton>
          ) : null}
        </Box>

        <Box display="flex" flexDirection="column">
          {event.players?.length > 0 ? (
            event.players.map((player) => {
              const membership = playerTeamById.get(player.id);
              const playerTeam = membership?.team;
              const isReserve = membership?.role === 'reserve';
              const removeDisabled = isReserve || (!isSingles && Boolean(playerTeam));
              const status = player.status || playerTeam?.status || 'Active';
              const statusReason = player.statusReason || playerTeam?.statusReason || '';

              return (
                <List dense key={player.id}>
                  <ListItem key={player.id} dense>
                    <Box
                      display={'flex'}
                      justifyContent={'space-between'}
                      alignItems={'center'}
                      width="100%">
                      <Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography>
                            {player.name} {player.surname}
                          </Typography>
                          <Chip
                            size="small"
                            label={status}
                            color={STATUS_COLOR[status] || 'default'}
                          />
                        </Box>
                        {playerTeam && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {isReserve ? 'Reserve' : 'Team'}: {playerTeam.name}
                          </Typography>
                        )}
                        {statusReason ? (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Reason: {statusReason}
                          </Typography>
                        ) : null}
                      </Box>
                      {!readOnly ? (
                        <Box>
                          {canReplacePlayer ? (
                            <Tooltip title="Replace Player">
                              <IconButton onClick={() => handlePlayerSwap(player)}>
                                <SwapHorizIcon color="info" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          <Tooltip title="Withdraw / Disqualify / Reactivate">
                            <IconButton onClick={() => handlePlayerStatus(player)}>
                              <FlagIcon color="warning" />
                            </IconButton>
                          </Tooltip>
                          <IconButton onClick={() => handlePlayerEdit(player)}>
                            <EditIcon color="primary" />
                          </IconButton>
                          <Tooltip
                            title={
                              isReserve
                                ? 'Player is a reserve. Remove them from the reserves list first.'
                                : removeDisabled
                                  ? 'Player is in a team. Remove from the team first.'
                                  : 'Remove player'
                            }>
                            <span>
                              <IconButton
                                disabled={removeDisabled}
                                onClick={() => handleRemoveConfirmationDialogOpen(player)}>
                                <TrashIcon color={removeDisabled ? 'disabled' : 'error'} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      ) : null}
                    </Box>
                  </ListItem>
                </List>
              );
            })
          ) : (
            <Typography variant="caption">No Players Found, add a player...</Typography>
          )}
        </Box>
      </Box>

      {!readOnly ? (
        <>
          <PlayerViewDialog
            event={event}
            player={selectedPlayer}
            open={openPlayer}
            setOpen={setOpenPlayer}
          />

          <PlayerStatusDialog
            open={openPlayerStatusDialog}
            onClose={() => setOpenPlayerStatusDialog(false)}
            player={playerForStatus}
            eventId={event.id}
          />

          <Dialog
            open={removeConfirmationDialogOpen}
            onClose={handleRemoveConfirmationDialogClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description">
            <DialogTitle id="alert-dialog-title">
              {'Your are about to delete this player'}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Are you sure you want to delete this player?
              </DialogContentText>
              <DialogContentText id="alert-dialog-description">
                {selectedPlayer?.name}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleRemoveConfirmationDialogClose}>Cancel</Button>
              <Button onClick={onPlayerRemove} autoFocus>
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : null}

      <PlayerSwapDialog
        playerToSwap={playerToSwap}
        open={openSwapDialog}
        onClose={handleSwapDialogClose}
        eventId={event.id}
      />
    </Fragment>
  );
};

export default PlayersPanel;
